import { UserStatus } from '@artham/shared';
import { useRef, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useTranslate } from 'src/locales';

import { toast } from 'src/components/snackbar';
import { Iconify } from 'src/components/iconify';

import { useAuthContext } from '../hooks';
import { signOut } from '../context/firebase/action';

// ----------------------------------------------------------------------

type UserStatusDialogProps = {
  status: UserStatus;
  onContactSupport?: () => void;
};

type StatusConfig = {
  illustration: string;
  title: string;
  message: string;
  color: string;
  bgColor: string;
  actions: string[];
};

type TFunc = (key: string) => string;

const getStatusConfig = (t: TFunc): Partial<Record<UserStatus, StatusConfig>> => ({
  [UserStatus.KYC_PENDING]: {
    illustration: '/assets/illustrations/characters/character-study.webp',
    title: t('statusDialog.kycPending.title'),
    message: t('statusDialog.kycPending.message'),
    color: 'warning.main',
    bgColor: 'warning.lighter',
    actions: ['openKyc', 'signout'],
  },
  [UserStatus.INVITED]: {
    illustration: '/assets/illustrations/characters/character-study.webp',
    title: t('statusDialog.invited.title'),
    message: t('statusDialog.invited.message'),
    color: 'info.main',
    bgColor: 'info.lighter',
    actions: ['signout'],
  },
  [UserStatus.DISABLED]: {
    illustration: '/assets/illustrations/characters/character-reject.webp',
    title: t('statusDialog.disabled.title'),
    message: t('statusDialog.disabled.message'),
    color: 'error.main',
    bgColor: 'error.lighter',
    actions: ['contact', 'signout'],
  },
  [UserStatus.KYC_UNDER_REVIEW]: {
    illustration: '/assets/illustrations/characters/character-study.webp',
    title: t('statusDialog.kycUnderReview.title'),
    message: t('statusDialog.kycUnderReview.message'),
    color: 'info.main',
    bgColor: 'info.lighter',
    actions: ['signout'],
  },
  [UserStatus.KYC_INFO_REQUESTED]: {
    illustration: '/assets/illustrations/characters/character-study.webp',
    title: t('statusDialog.kycInfoRequested.title'),
    message: t('statusDialog.kycInfoRequested.message'),
    color: 'warning.main',
    bgColor: 'warning.lighter',
    actions: ['openKyc', 'signout'],
  },
  [UserStatus.KYC_REJECTED]: {
    illustration: '/assets/illustrations/characters/character-reject.webp',
    title: t('statusDialog.kycRejected.title'),
    message: t('statusDialog.kycRejected.message'),
    color: 'error.main',
    bgColor: 'error.lighter',
    actions: ['openKyc', 'signout'],
  },
});

export function UserStatusDialog({ status, onContactSupport }: UserStatusDialogProps) {
  const { t } = useTranslate('auth');

  const router = useRouter();
  const { checkUserSession } = useAuthContext();
  const pollCountRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Polling with exponential backoff: 10s, 20s, 40s, 80s, 160s, then cap at 160s
  useEffect(() => {
    // Only poll for KYC_PENDING or DISABLED status
    if (status !== UserStatus.KYC_PENDING && status !== UserStatus.DISABLED) {
      // Reset poll count when status changes away from polling statuses
      pollCountRef.current = 0;
      return undefined;
    }

    const pollStatus = async () => {
      try {
        await checkUserSession?.();
      } catch (error) {
        console.error('Error polling user status:', error);
      }
    };

    const scheduleNextPoll = () => {
      // Calculate delay: first poll after 10s, then 20s, 40s, 80s, 160s, then cap at 160s
      const delays = [10, 20, 40, 80, 160];
      const delayIndex = Math.min(pollCountRef.current, delays.length - 1);
      const delaySeconds = delays[delayIndex];

      timeoutRef.current = setTimeout(() => {
        pollCountRef.current += 1;
        pollStatus();
        scheduleNextPoll();
      }, delaySeconds * 1000);
    };

    // Reset poll count when starting
    pollCountRef.current = 0;

    // Start polling immediately
    pollStatus();
    scheduleNextPoll();

    // Cleanup on unmount or status change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [status, checkUserSession]);

  const handleContactSupport = () => {
    if (onContactSupport) {
      onContactSupport();
    } else {
      // Default: Open email client
      window.location.href = 'mailto:support@artham.com';
    }
  };

  const handleOpenKyc = useCallback(() => {
    router.push(paths.dashboard.artham.kyc.root);
  }, [router]);

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
      await checkUserSession?.();
      router.refresh();
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error(t('statusDialog.signOutError'));
    }
  }, [checkUserSession, router, t]);

  // Don't show dialog for UNKNOWN_USER or ACTIVE
  if (status === UserStatus.UNKNOWN_USER || status === UserStatus.ACTIVE) {
    return null;
  }

  const config = getStatusConfig(t)[status];

  if (!config) {
    return null;
  }

  return (
    <Dialog open fullScreen disableEscapeKeyDown>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 4,
          textAlign: 'center',
        }}
      >
        {/* Illustration */}
        <Box
          component="img"
          src={config.illustration}
          alt={config.title}
          sx={{
            width: 240,
            height: 240,
            mb: 3,
            objectFit: 'contain',
          }}
        />

        {/* Status Icon Badge */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor:
              status === UserStatus.KYC_PENDING
                ? (theme) => alpha(theme.palette.warning.main, 0.16)
                : (theme) => alpha(theme.palette.error.main, 0.16),
            mb: 3,
          }}
        >
          <Iconify
            icon={
              status === UserStatus.KYC_PENDING
                ? 'solar:clock-circle-bold'
                : 'solar:close-circle-bold'
            }
            width={32}
            sx={{ color: config.color }}
          />
        </Box>

        {/* Title */}
        <Typography variant="h4" sx={{ mb: 2 }}>
          {config.title}
        </Typography>

        {/* Message */}
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 480 }}>
          {config.message}
        </Typography>

        {/* Additional Info for KYC_PENDING */}
        {status === UserStatus.KYC_PENDING && (
          <Box
            sx={{
              p: 2,
              borderRadius: 1.5,
              bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
              border: (theme) => `1px solid ${alpha(theme.palette.warning.main, 0.24)}`,
              mb: 4,
              maxWidth: 480,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Iconify
                icon="solar:info-circle-bold"
                width={20}
                sx={{ color: 'warning.main', mt: 0.25 }}
              />
              <Typography variant="body2" color="text.secondary" textAlign="left">
                {t('statusDialog.kycPending.approvalInfo')}
              </Typography>
            </Stack>
          </Box>
        )}

        {/* Actions */}
        <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
          {config.actions.includes('openKyc') && (
            <Button
              variant="contained"
              size="large"
              color="warning"
              onClick={handleOpenKyc}
              startIcon={<Iconify icon="solar:document-add-bold" />}
              sx={{ minWidth: 180 }}
            >
              {t('statusDialog.openKyc')}
            </Button>
          )}

          {config.actions.includes('contact') && (
            <Button
              variant="contained"
              size="large"
              color="error"
              onClick={handleContactSupport}
              startIcon={<Iconify icon="solar:chat-round-dots-bold" />}
              sx={{ minWidth: 180 }}
            >
              {t('statusDialog.contactSupport')}
            </Button>
          )}

          {config.actions.includes('signout') && (
            <Button
              variant="outlined"
              size="large"
              color="inherit"
              onClick={handleSignOut}
              startIcon={<Iconify icon={'solar:logout-2-bold' as any} />}
              sx={{ minWidth: 140 }}
            >
              {t('statusDialog.signOut')}
            </Button>
          )}
        </Stack>

        {/* Footer note for DISABLED */}
        {status === UserStatus.DISABLED && (
          <Typography variant="caption" color="text.disabled" sx={{ mt: 3, maxWidth: 400 }}>
            {t('statusDialog.disabled.footerNote')}
          </Typography>
        )}
      </Box>
    </Dialog>
  );
}
